import React, { useState, createContext, useContext } from 'react';
interface Package {
  id: string;
  length: number;
  width: number;
  height: number;
  description?: string;
  imageUrl?: string;
}
interface OrderDetails {
  pickupAddress: string;
  dropoffAddress: string;
  moveDate: string;
  moveTime: string;
  contactName: string;
  contactPhone: string;
  contactEmail: string;
  packages: Package[];
  vehicleSize: 'small' | 'medium' | 'large';
  extraMovers: number;
  packingService: boolean;
  insurance: boolean;
  estimatedPrice: number;
  status: 'draft' | 'confirmed' | 'in-progress' | 'completed';
  confirmationNumber?: string;
}
interface OrderContextType {
  orderDetails: OrderDetails;
  updateOrderDetails: (details: Partial<OrderDetails>) => void;
  addPackage: (pkg: Omit<Package, 'id'>) => void;
  updatePackage: (id: string, pkg: Partial<Package>) => void;
  removePackage: (id: string) => void;
  calculatePrice: () => number;
  resetOrder: () => void;
  confirmOrder: () => void;
}
const defaultOrderDetails: OrderDetails = {
  pickupAddress: '',
  dropoffAddress: '',
  moveDate: '',
  moveTime: '',
  contactName: '',
  contactPhone: '',
  contactEmail: '',
  packages: [],
  vehicleSize: 'medium',
  extraMovers: 0,
  packingService: false,
  insurance: false,
  estimatedPrice: 0,
  status: 'draft'
};
const OrderContext = createContext<OrderContextType | undefined>(undefined);
export const OrderProvider: React.FC<{
  children: React.ReactNode;
}> = ({
  children
}) => {
  const [orderDetails, setOrderDetails] = useState<OrderDetails>(defaultOrderDetails);
  const updateOrderDetails = (details: Partial<OrderDetails>) => {
    setOrderDetails(prev => {
      const updated = {
        ...prev,
        ...details
      };
      // Recalculate price whenever order details change
      updated.estimatedPrice = calculatePrice();
      return updated;
    });
  };
  const addPackage = (pkg: Omit<Package, 'id'>) => {
    const newPackage = {
      ...pkg,
      id: `pkg-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
    };
    setOrderDetails(prev => {
      const updated = {
        ...prev,
        packages: [...prev.packages, newPackage]
      };
      updated.estimatedPrice = calculatePrice();
      return updated;
    });
  };
  const updatePackage = (id: string, pkg: Partial<Package>) => {
    setOrderDetails(prev => {
      const updated = {
        ...prev,
        packages: prev.packages.map(p => p.id === id ? {
          ...p,
          ...pkg
        } : p)
      };
      updated.estimatedPrice = calculatePrice();
      return updated;
    });
  };
  const removePackage = (id: string) => {
    setOrderDetails(prev => {
      const updated = {
        ...prev,
        packages: prev.packages.filter(p => p.id !== id)
      };
      updated.estimatedPrice = calculatePrice();
      return updated;
    });
  };
  const calculatePrice = () => {
    // Basic calculation logic (simplified for demo)
    const basePrice = 50; // Base price in EUR
    // Vehicle size pricing
    const vehiclePricing = {
      small: 50,
      medium: 100,
      large: 150
    };
    // Calculate volume of all packages
    const totalVolume = orderDetails.packages.reduce((acc, pkg) => {
      return acc + pkg.length * pkg.width * pkg.height;
    }, 0);
    // Calculate distance factor (simplified)
    let distanceFactor = 1;
    if (orderDetails.pickupAddress && orderDetails.dropoffAddress) {
      // In a real app, we would calculate actual distance
      distanceFactor = 1.5;
    }
    // Additional services
    const extraMoversPrice = orderDetails.extraMovers * 25;
    const packingPrice = orderDetails.packingService ? 75 : 0;
    const insurancePrice = orderDetails.insurance ? 50 : 0;
    // Calculate total
    const total = basePrice + vehiclePricing[orderDetails.vehicleSize] + totalVolume * 0.001 +
    // Price per cubic cm
    distanceFactor * 20 +
    // Distance factor
    extraMoversPrice + packingPrice + insurancePrice;
    return Math.round(total);
  };
  const resetOrder = () => {
    setOrderDetails(defaultOrderDetails);
  };
  const confirmOrder = () => {
    setOrderDetails(prev => ({
      ...prev,
      status: 'confirmed',
      confirmationNumber: `MOV-${Date.now().toString().substr(-6)}`
    }));
  };
  return <OrderContext.Provider value={{
    orderDetails,
    updateOrderDetails,
    addPackage,
    updatePackage,
    removePackage,
    calculatePrice,
    resetOrder,
    confirmOrder
  }} data-id="element-80">
      {children}
    </OrderContext.Provider>;
};
export const useOrder = () => {
  const context = useContext(OrderContext);
  if (context === undefined) {
    throw new Error('useOrder must be used within an OrderProvider');
  }
  return context;
};